import * as Sentry from "@sentry/react";
import {
  subscribeToReceiveMessageFromSoundsliceIframeEvents
} from "../eventBus/soundslice/receiveMessageFromSoundsliceIframeEventBus";
import {SoundsliceResponseEventTypes} from "../eventBus/soundslice/SoundsliceResponseEventTypes";
import {
  soundSliceSdk_getNotesAsync as iframeGetNotes,
  soundSliceSdk_setVideoPosition,
} from "../eventBus/soundslice/soundSliceSdk";
import {
  lessonLoaded as lessonLoadedEvent,
  segmentChanged,
  segmentNotesChanged
} from "../eventBus/segment/segmentEventBus";
import Segment from "../../models/view/lessonPage/Segment";
import Note from "../../models/soundslice/Note";
import Lesson from "../../models/view/lessonPage/Lesson";
import {IRouteChanged, subscribeToRouteChanged} from "../eventBus/route/routeEventBus";
import {appRoutePaths, lessonPageUrlFragment} from "../../config/config";
import playgroundApi from "../playgroundApi";
import {
  createLessonUrl,
  createLessonViewModelFromApiResponse,
  createSoundsliceIframeUrl
} from "../../factories/lessonPageFactory";
import {play} from "../../redux/lesson/lessonActions";
import {displayErrorToUser, withDisplayErrorToUser} from "../error/error";
import "../scorer/noteScorer";
import "../scorer/performanceScorer";
import "../scorer/videoScorer";
import PitchDetector from "./pitchDetector";
import {subscribeToScorerEvents} from "../eventBus/scorer/scorerEventBus";
import {ScorerEventTypes} from "../eventBus/scorer/ScorerEventTypes";
import {IPerformanceScore} from "../../models/scorer/IPerformanceScore";
import AddPracticeRecordRequest from "../../client/models/practiceRecords/AddPracticeRecordRequest";
import {subscribeToReceiveMessagesFromNativeApp} from "../eventBus/nativeApp/receiveMessageFromNativeAppEventBus";
import {FromNativeAppEventTypes} from "../eventBus/nativeApp/FromNativeAppEventTypes";
import settingsService from "../settings/settingsService";
import AddPracticeRecordResponse from "../../client/models/practiceRecords/AddPracticeRecordResponse";
import IScoreModal from "../../models/view/lessonPage/IScoreModal";
import {SegmentModeEnum} from "../../models/view/lessonPage/SegmentModeEnum";
import IVideoScore from "../../models/scorer/IVideoScore";
import {getItem, setItem} from "../device/localStorage";
import {navigate} from "../navigation/navigation";
import deviceInfo from "../deviceInfo/deviceInfo";
import {logger} from "../log/logger";
import type {DebouncedFuncLeading} from "lodash";
import debounce from "lodash/debounce"
import nativeBridge from "../nativeBridge";
import {SoundSliceVideoPositionEnum} from "../soundSlice/soundSliceVideoPositionEnum";
import {NoteEventTime} from "@playground-sessions/pitch-detection/types/toMidi";
import orchestrator from "../../services/playbackOrchestrator/playbackOrchestrator";
import {subscribeToNotesEvents} from "../eventBus/notes/notesEventBus";
import {NotesEventTypes} from "../eventBus/notes/NotesEventTypes";
import noteScorer from "../scorer/noteScorer";
import {deepCopyArray} from "../../util/array-util";
import {sortNotesByPitchThenByStartTime} from "./detectedNoteCleanupUtility";
import {lessonLocalStorage} from "./lessonLocalStorage";
import segmentDataProvider from "../../redux/lesson/SegmentDataProvider";
import {lessonPlayerStore} from "../../redux/lesson/LessonPlayerStore";
import {settingsStore} from "../../redux/settings/settingsStore";
import {MidiDeviceSettings} from "../../models/view/lessonPage/MidiDeviceSettings";
import {getOrientation} from "../../util/screenUtil";
import {getNotationLayoutToApplyFromSelectedNotationLayout} from "./lessonUtility";
import {getUserPreferencesForControlBar} from "../UserPreferencesForControlBarService";
import {keyboardWizardStore} from "../../redux/keyboardWizard/keyboardWizardStore";
import {subscribeToMidiEvents} from "../eventBus/midi/midiEventBus";
import {MidiEventTypes} from "../eventBus/midi/MidiEventTypes";
import {MidiInputSource} from "../eventBus/midi/MidiEventTypes";
import ILessonResponse from "../../client/models/lessonWithSingleModeSegments/ILessonResponse";
import LessonPermissionsError from "../../client/errors/LessonPermissionsError";
import {transcriptionBenchmark} from "./transcriptionBenchmark";

const lessonPageReferrerLocalStorageKey = "lessonPageReferrer";

/**
 * This file shouldn't exist. This does not serve the traditional role of a service class but is instead very tightly
 * coupled to the lesson page. We want to do the following
 * Move presenter-level stuff into custom hooks
 * Move service-level stuff into lessonUtility.ts
 */

/**
 * Responsible for tying together data about a segment we get from Soundslice (e.g. Notes) and data about a segment we get from PS api.
 * Listen for and orchestrate Soundslice events.
 * Listen for route changes and orchestrate fetching lesson data, notifying others about segment and notes for segment changes (e.g. metronome and notesPlayer)
 */
class LessonService {
  //array of unsubscribe functions to call when this instance is destroyed.
  unsubscribeFuncs: (() => void)[] = [];
  //cached version of a lesson, so we have it between segment url changes.
  lesson?: Lesson;
  //object which houses all data related to a segment, including measures, time signatures, etc.
  segment?: Segment;
  //notes for the segment, provided by Soundslice. Ultimately this should probably end up in Segment -> Measures -> Notes
  notes?: Note[];
  displayProgressBarTimeoutId: number;
  pitchDetector: PitchDetector;
  // Track the previous location path to determine where the user came from
  previousLocationPath?: string;

  // Physical keyboards can have multiple midi inputs. When a physical keyboard is plugged in onKeyboardPluggedIn will
  // get called multiple times in rapid succession (once for each input). Will use these settings to determine if one of the
  // inputs was a previously configured keyboard.
  keyboardConnectedTimeout: ReturnType<typeof setTimeout> | undefined;
  keyboardPluginThreshold: number = 0;
  lastTimeAConfiguredKeyboardWasPluggedIn: number = 0;

  // MIDI event subscription - tracked separately so we can unsubscribe when leaving the lesson page
  #unsubscribeFromMidiEvents: (() => void) | null = null;

  private readonly debouncedHandleVideoScoringCompleted: DebouncedFuncLeading<(videoScore: IVideoScore) => Promise<void>>;
  constructor() {
    this.#setupNotesEventRouter();

    this.debouncedHandleVideoScoringCompleted = debounce(this.onVideoScoring_completed.bind(this), 5000, {
      /**
       * If leading and trailing options are true,
       * func is invoked on the trailing edge of the timeout
       * only if the debounced function is invoked
       * more than once during the wait timeout.
       * @see https://lodash.com/docs/4.17.15#debounce
       */
      leading: true,
      trailing: false,
    });

    this.unsubscribeFuncs = [
      subscribeToReceiveMessageFromSoundsliceIframeEvents(this.onMessageFromSoundslice_received.bind(this)),
      subscribeToRouteChanged(this.onRoute_changed.bind(this)),
      subscribeToScorerEvents(this.onScorer_eventRaised.bind(this)),
      subscribeToReceiveMessagesFromNativeApp(this.onNativeApp_eventRaised.bind(this)),
    ];
  }

  destroy(){
    this.unsubscribeFuncs.forEach(u => u());
  }

  // Consider replacing these with a hook paired with a component, similar to usePitchDetectionStatus.hook.ts
  #setupReduxListeners(): void {
    logger.pitchDetection.info({
      message: "setupReduxListeners",
      showStack: false,
    });
    segmentDataProvider.subscribeToIsDemoSoundEnabledChanged(this.#onIsDemoSoundEnabled_changed.bind(this), true);
  }

  #onIsDemoSoundEnabled_changed(isDemoSoundEnabled: boolean): void {
    logger.pitchDetection.info({
      message: "onIsDemoSoundEnabled_changed",
      showStack: false,
      data: [ {key: "isDemoSoundEnabled", value: isDemoSoundEnabled} ]
    });

    this.#updateCanShowPitchDetectionDisabledDueToDemoSoundEnabledMessage();
  }

  #updateCanShowPitchDetectionDisabledDueToDemoSoundEnabledMessage(): void {
    const isDemoSoundEnabled = lessonPlayerStore.getIsDemoLeftHandSoundEnabled() || lessonPlayerStore.getIsDemoRightHandSoundEnabled();
    const hasPitchDetectionDemoSoundEnabledMessageBeenDisplayed = lessonLocalStorage.getOrCreateHasPitchDetectionDemoSoundEnabledMessageBeenDisplayed(false);
    const isMidiConnected = lessonPlayerStore.getIsMidiConnected();
    const canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage = isDemoSoundEnabled
      && !hasPitchDetectionDemoSoundEnabledMessageBeenDisplayed
      && !isMidiConnected;

    logger.pitchDetection.info({
      message: "updateCanShowPitchDetectionDisabledDueToDemoSoundEnabledMessage",
      showStack: false,
      data: [
        { key: "isDemoSoundEnabled", value: isDemoSoundEnabled },
        { key: "hasPitchDetectionDemoSoundEnabledMessageBeenDisplayed", value: hasPitchDetectionDemoSoundEnabledMessageBeenDisplayed },
        { key: "isMidiConnected", value: isMidiConnected },
        { key: "canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage", value: canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage },
      ]
    });

    lessonPlayerStore.setCanShowPitchDetectionDisabledDueToDemoSoundEnabledMessage(canShowPitchDetectionDisabledDueToDemoSoundEnabledMessage);
  }

  #setupMidiDevicesListener(isFreshLoad: boolean) {
    logger.pitchDetection.info({
      message: "setupKeyboardListener",
      showStack: false
    });

    // Clean up any existing subscription before creating a new one
    // (handles case where user navigates between lesson pages without leaving)
    if (this.#unsubscribeFromMidiEvents) {
      this.#unsubscribeFromMidiEvents();
      this.#unsubscribeFromMidiEvents = null;
    }

    // Subscribe to change in MIDI devices
    this.#unsubscribeFromMidiEvents = subscribeToMidiEvents(this.#routeMidiEvent.bind(this), `LessonService`);

    // Make sure Redux is updated
    lessonPlayerStore.setIsMidiConnected(settingsStore.getMidiDeviceOptions().length > 0);

    // Check for already-connected unconfigured devices and show wizard if needed
    this.#showWizardForAlreadyConnectedUnconfiguredDevices(isFreshLoad);

    // Stuff to update debugging UI
    const isPitchDetectionDebuggingEnabled = settingsStore.getIsPitchDetectionDebuggingEnabled();

    if (isPitchDetectionDebuggingEnabled) {
      const getIsSelectedDeviceConnectedAsync = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
          const selectedDeviceId = settingsStore.getSelectedMidiDeviceId();
          const connectedDevices = settingsStore.getMidiDeviceOptions();
          // Check if the selected device is connected
          const selectedDeviceIsConnected = connectedDevices.filter((device) => {
            return device.getId() === selectedDeviceId.toString();
          }).length > 0
          resolve(selectedDeviceIsConnected);
        });
      };
      this.#updateIndicator("selected-keyboard-is-plugged-in", getIsSelectedDeviceConnectedAsync);

      const getIsAnyMidiDeviceConnectedAsync = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
          resolve(settingsStore.getMidiDeviceOptions().length > 0);
        });
      };
      this.#updateIndicator("keyboard-is-plugged-in", getIsAnyMidiDeviceConnectedAsync);
    }
  }

  /**
   * Determine whether to show the keyboard wizard or "Keyboard Connected" modal
   * when the user enters the lesson page with MIDI devices already connected.
   *
   * Decision logic (per product requirements):
   * 1. If the currently selected device is connected → show modal (only on fresh load), don't show wizard
   * 2. If selected device is not connected (or no device selected):
   *    a. If there are unconfigured devices → show wizard
   *    b. If all connected devices are configured → auto-select one and show modal (only on fresh load)
   *
   * @param isFreshLoad - true if this is a fresh app load (not in-app navigation).
   *                      The modal is suppressed on in-app navigation to avoid repeatedly
   *                      interrupting the user when switching between songs.
   */
  #showWizardForAlreadyConnectedUnconfiguredDevices(isFreshLoad: boolean): void {
    const connectedDevices = settingsStore.getMidiDeviceOptions();

    if (connectedDevices.length === 0) {
      return; // No devices connected, nothing to do
    }

    // Step 1: Check if the currently SELECTED device is connected
    const selectedDeviceId = settingsStore.getSelectedMidiDeviceId();
    const isSelectedDeviceConnected = selectedDeviceId.length > 0 && connectedDevices.some(
      device => device.getId() === selectedDeviceId
    );

    if (isSelectedDeviceConnected) {
      // Selected device is connected - show modal on fresh load only, don't show wizard
      if (isFreshLoad) {
        lessonPlayerStore.setCanShowKeyboardConnectedModal(true);
      }
      return;
    }

    // Step 2: Selected device not connected - check for unconfigured devices
    let hasUnconfiguredDevice = false;
    let firstConfiguredDevice: MidiDeviceSettings | undefined;

    for (const device of connectedDevices) {
      const deviceSettings = lessonLocalStorage.getMidiDeviceSettingsById(device.getId());
      if (deviceSettings) {
        if (!firstConfiguredDevice) {
          firstConfiguredDevice = deviceSettings;
        }
      } else {
        hasUnconfiguredDevice = true;
      }
    }

    if (hasUnconfiguredDevice) {
      // There are unconfigured devices - show wizard
      keyboardWizardStore.reset();
      keyboardWizardStore.setIsOpen(true);
    } else if (firstConfiguredDevice) {
      // No unconfigured devices, but there's another configured device available
      // Auto-select it and show the connected modal (only on fresh load)
      settingsStore.setSelectedMidiDevice(firstConfiguredDevice.id, firstConfiguredDevice.name);
      if (isFreshLoad) {
        lessonPlayerStore.setCanShowKeyboardConnectedModal(true);
      }
    }
  }

  /**
   * This method is called once for each midi input when a physical keyboard is plugged in
   */
  #onKeyboardPluggedIn(deviceSettings: MidiDeviceSettings | undefined) {
    // Section: enable feedback
    lessonPlayerStore.setIsInstantFeedbackEnabled(true);

    // Section: hide keyboard disconnected modal
    lessonPlayerStore.setCanShowKeyboardDisconnectedModal(false);
    lessonPlayerStore.setCanShowSwitchToPitchDetectionPrompt(false);

    // Section: if a configured device was plugged in, set it as the selected device. Also set the time the device was plugged in.
    const currentTime = new Date().getTime();
    const wasAConfiguredDevicePluggedIn = !!deviceSettings;
    if (wasAConfiguredDevicePluggedIn) {
      // If you plug in a physical device with multiple configured inputs, the last input will be the one that is set as selected.
      settingsStore.setSelectedMidiDevice(deviceSettings.id, deviceSettings.name);
      this.lastTimeAConfiguredKeyboardWasPluggedIn = currentTime;
    }

    // Section: Show either keyboard connected modal or keyboard wizard
    const timeWindowSizeInMs = 1000;

    const shouldUpdateKeyboardPluginThreshold = this.keyboardPluginThreshold === 0 || this.keyboardPluginThreshold < new Date().getTime() - 1500;
    if (shouldUpdateKeyboardPluginThreshold) {
      // Add a small buffer to make sure we consider all the inputs
      const buffer = 1;
      this.keyboardPluginThreshold = currentTime - buffer;

      // Make sure the lastTimeAConfiguredKeyboardWasPluggedIn value wasn't from a previous plugin
      if (this.lastTimeAConfiguredKeyboardWasPluggedIn < this.keyboardPluginThreshold) {
        this.lastTimeAConfiguredKeyboardWasPluggedIn = 0;
      }
    }

    // Why this delay? Well cuz when you plug in a physical keyboard, it may have multiple midi inputs. This method
    // will get called once for each input. We don't want the Keyboard Connected and Keyboard Wizard appearing and
    // disappearing in the UI cuz that looks bad. So we wait timeWindowSizeInMs to figure out if any of the
    // inputs are preconfigured then decide what to show.
    if (this.keyboardConnectedTimeout) {
      clearTimeout(this.keyboardConnectedTimeout);
    }

    this.keyboardConnectedTimeout = setTimeout(() => {
      const wasConfiguredDevicePluggedInRecently = this.lastTimeAConfiguredKeyboardWasPluggedIn > this.keyboardPluginThreshold;
      if (wasConfiguredDevicePluggedInRecently) {
        lessonPlayerStore.setCanShowKeyboardConnectedModal(true);
      } else {
        // Unconfigured device was hot-plugged - show wizard.
        // Per product requirements: hot-plugging is an intentional user action,
        // so we show the wizard even if a configured keyboard is already connected.
        keyboardWizardStore.reset();
        keyboardWizardStore.setIsOpen(true);
      }
    }, timeWindowSizeInMs);
  }

  #onKeyboardUnplugged(_midiPort: MidiInputSource | undefined) {
    lessonPlayerStore.setCanShowKeyboardConnectedModal(false);
    const soundDemoIsEnabled = lessonPlayerStore.getIsDemoLeftHandSoundEnabled() || lessonPlayerStore.getIsDemoRightHandSoundEnabled();

    if (soundDemoIsEnabled) {
      lessonPlayerStore.setCanShowSwitchToPitchDetectionPrompt(true);
    } else {
      lessonPlayerStore.setCanShowKeyboardDisconnectedModal(true);
    }
  }

  /**
   * This method is called when all MIDI devices are unplugged
   */
  #onAllMidiDevices_unplugged() {
    keyboardWizardStore.setIsOpen(false);
  }

  /**
   * This method is called when a MIDI device is connected / disconnected
   * NOTE: Some MIDI controllers / pianos have multiple inputs. This method will be called for each input.
   */
  async #onMidiDevices_changed(midiInput: MidiInputSource | undefined) {
    let midiDeviceWasPluggedIn = false;

    // Depending on the OS, midiPort may be undefined when a midi device is unplugged.
    if (midiInput) {
      // For WebMIDI (NormalizedMidiInput), check the state property
      // For native MIDI (NativeMidiInput), if we have the input object, it's connected
      if ('getMidiInput' in midiInput) {
        midiDeviceWasPluggedIn = midiInput.getMidiInput().state === "connected";
      } else {
        // NativeMidiInput - if we received it in an event, it's connected
        midiDeviceWasPluggedIn = true;
      }
      const deviceSettings = lessonLocalStorage.getMidiDeviceSettingsById(midiInput.getId());

      // Section: raise onKeyboardPluggedIn event if a keyboard was plugged in
      if (midiDeviceWasPluggedIn) {
        this.#onKeyboardPluggedIn(deviceSettings);
      }
    }

    // Section: raise onKeyboardUnplugged event if a keyboard was unplugged
    if (!midiDeviceWasPluggedIn) {
      this.#onKeyboardUnplugged(midiInput);
    }

    // Section: Update Redux state
    const isAnyMidiDeviceConnected = settingsStore.getMidiDeviceOptions().length > 0;
    lessonPlayerStore.setIsMidiConnected(isAnyMidiDeviceConnected);

    // Section: if no MIDI devices are detected, raise onAllMidiDevices_unplugged event
    if (!isAnyMidiDeviceConnected) {
      this.#onAllMidiDevices_unplugged();
    }

    // Section: Update UI pitch detection debugging elements
    this.#updateUIItemsAffectedByMidiDevices();

    // Section: Show/hide pitch detection disabled due to demo mode message if applicable
    this.#updateCanShowPitchDetectionDisabledDueToDemoSoundEnabledMessage();
  }

  #updateUIItemsAffectedByMidiDevices() {
    const isPitchDetectionDebuggingEnabled = settingsStore.getIsPitchDetectionDebuggingEnabled();

    if (isPitchDetectionDebuggingEnabled) {
      const getIsSelectedDeviceConnectedAsync = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
          const selectedDeviceId = settingsStore.getSelectedMidiDeviceId();
          const connectedDevices = settingsStore.getMidiDeviceOptions();
          // Check if the selected device is connected
          const selectedDeviceIsConnected = connectedDevices.filter((device) => {
            return device.getId() === selectedDeviceId.toString();
          }).length > 0
          resolve(selectedDeviceIsConnected);
        });
      };
      this.#updateIndicator("selected-keyboard-is-plugged-in", getIsSelectedDeviceConnectedAsync);

      const getIsAnyMidiDeviceConnectedAsync = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
          const isAnyMidiDeviceConnected = settingsStore.getIsMidiDeviceConnected();
          resolve(isAnyMidiDeviceConnected);
        });
      };
      this.#updateIndicator("keyboard-is-plugged-in", getIsAnyMidiDeviceConnectedAsync);
    }
  }

  #syncLocalStorageValuesWithRedux(): void {
    // Local storage should be the source of truth.

    // Get values from local storage
    const isPitchDetectionDebuggingEnabledFromStorage = lessonLocalStorage.getOrCreatePitchDetectionDebuggingIsEnabled(false);
    const userPreferencesForControlBar = getUserPreferencesForControlBar();
    const isFeedbackEnabledFromStorage = userPreferencesForControlBar.isInstantFeedbackEnabled;

    // Get values from memory
    const isPitchDetectionDebuggingEnabledFromRedux = settingsStore.getIsPitchDetectionDebuggingEnabled();
    const isFeedbackEnabledFromRedux = lessonPlayerStore.getIsInstantFeedbackEnabled();

    // Compare values
    const isPitchDetectionDebuggingEnabledChanged = isPitchDetectionDebuggingEnabledFromStorage !== isPitchDetectionDebuggingEnabledFromRedux
    const isFeedbackEnabledChanged = isFeedbackEnabledFromStorage !== isFeedbackEnabledFromRedux;

    logger.pitchDetection.info({
      message: "syncPitchDetectionSettings",
      showStack: false,
      data: [
        { key: "isPitchDetectionDebuggingEnabledFromStorage", value: isPitchDetectionDebuggingEnabledFromStorage },
        { key: "isPitchDetectionDebuggingEnabledFromRedux", value: isPitchDetectionDebuggingEnabledFromRedux },
        { key: "isPitchDetectionDebuggingEnabledChanged", value: isPitchDetectionDebuggingEnabledChanged },
        { key: "isFeedbackEnabledFromStorage", value: isFeedbackEnabledFromStorage },
        { key: "isFeedbackEnabledFromRedux", value: isFeedbackEnabledFromRedux },
        { key: "isFeedbackEnabledChanged", value: isFeedbackEnabledChanged },
      ]
    });

    if (isPitchDetectionDebuggingEnabledChanged) {
      settingsStore.setIsPitchDetectionDebuggingEnabled(isPitchDetectionDebuggingEnabledFromStorage);
    }

    if (isFeedbackEnabledChanged) {
      lessonPlayerStore.setIsInstantFeedbackEnabled(isFeedbackEnabledFromStorage);
    }
  }

  #setupNotesEventRouter(): void {
    this.unsubscribeFuncs = [
      subscribeToNotesEvents(this.#routeNotesEvent.bind(this)),
    ];
  }

  #routeMidiEvent(message : MidiEventTypes) {
    switch (message.type) {
      case "midiDeviceStateChange":
        this.#onMidiDevices_changed(message.port);
    }
  }

  #routeNotesEvent(e: NotesEventTypes){
    switch (e.type) {
      case "playbackStarted": // Received when the notes / backing track start playing
        this.#onPlayback_started();
        break;
      case "playback_ended": // Received when playback is manually stopped or ends naturally
        this.#onPlayback_ended(e.shouldScore);
        break;
      case "recordingStarted": // Received after microphone permission is received and recording has started.
        this.#onRecording_started();
        break;
    }
  }

  #onRecording_started() {
    // For some reason, this is the pathway that iOS uses to start playback.
    // Other platforms like the web don't use it.
    // Android may or may not use it.
    // Desktop usage is also undetermined.
    const isPlaying = lessonPlayerStore.getIsPlaying();
    const isCountInPlaying = lessonPlayerStore.getIsCountInPlaying();
    const isPitchDetectionEnabled = lessonPlayerStore.getIsPitchDetectionEnabled();
    const isPlaybackStarted = isPlaying || isCountInPlaying;

    if (!isPlaybackStarted && isPitchDetectionEnabled) {
      play();
    }
  }

  #onPlayback_started(){
    // Code that should run when playback has started goes here.
  }

  #onPlayback_ended(shouldScore: boolean){
    this.pitchDetector?.stopListening(shouldScore);
  }

  async onNativeApp_eventRaised(e: FromNativeAppEventTypes){
    const displayProgressBarForNms = 5000;

    switch(e.type){
      case "native.screenTouched":
        //immediately triggering isProgressBarShown ends up cancelling the click event somehow, even though the controlbar and button are definitely not getting re-rendered.  wait 200ms so the pause button's click handler fires.
        await new Promise((resolve)=> setTimeout(resolve, 200));

        lessonPlayerStore.setIsProgressBarShowing(true);
        clearTimeout(this.displayProgressBarTimeoutId);

        this.displayProgressBarTimeoutId = window.setTimeout(()=>{
          const isPlaying = lessonPlayerStore.getIsPlaying();
          if(isPlaying) {
            lessonPlayerStore.setIsProgressBarShowing(false);
          }
        }, displayProgressBarForNms);
        break;
    }
  }

  onScorer_eventRaised(e: ScorerEventTypes){
    switch (e.type) {
      case "performanceScoringCompleted":
        return this.onPerformanceScoring_completed(e.performanceScore);
      case "videoScoringCompleted":
        return this.debouncedHandleVideoScoringCompleted(e.videoScore);
    }
  }

  /**
   * DO NOT USE DIRECTLY!!
   * Should be called via this.debouncedHandleVideoScoringCompleted
   * @param videoScore
   * @private
   */
  private async onVideoScoring_completed(videoScore: IVideoScore){
    // Bug 324: App - error saving practice records (https://dev.azure.com/playgroundsessions/Playground/_workitems/edit/324)
    // If the user is on the lesson page and does something to trigger a practice record creation and navigates away
    // quickly, this can result in a race condition and this.lesson may be undefined. Example: User starts a video,
    // user stops a video and then quickly goes back to bootcamp / courses page.
    // So we need to check if we're still on the lesson page after every async call.
    await withDisplayErrorToUser(async () => {
      const initialSegmentId = this.segment?.id;

      const practiceRecord = createPracticeRecordFromVideoScore(videoScore);
      logger.lesson.log({message: `sending video score practice record` + practiceRecord.toString()});

      const scoringMechanism = lessonPlayerStore.getIsPitchDetectionEnabled() ? "PITCH_DETECTION" : "MIDI";
      await playgroundApi.addPracticeRecord({practiceRecord}, scoringMechanism);

      const isOnAndroid = deviceInfo.getIsAndroid();

      if (orchestrator.isCursorNearTheEndOfTheSegment() && !isOnAndroid) {
        const currentSegmentId = this.segment?.id;

        // User may have navigated away from the lesson page being scored. So only send the signal to show the modal
        // if the segmentId has not changed.
        if (initialSegmentId === currentSegmentId) {
          lessonPlayerStore.setDisplayLessonEndsWithVideoModal(true);
        }
      }

      await this.updateSegmentDropdownScores();
    }, "We ran into an error saving your practice record");
  }
 
  isUserOnLessonPage = (): boolean => {
    return !!this.lesson && !!this.segment && window.location.href.includes(lessonPageUrlFragment);
  }

  async onPerformanceScoring_completed(performanceScore: IPerformanceScore) {
    lessonPlayerStore.setScoreModalViewModel(undefined); //wipe out existing data
    await withDisplayErrorToUser(async () => {
      transcriptionBenchmark.markScoreModalShown();
      lessonPlayerStore.setDisplayScoreModal(true);

      const practiceRecord = createPracticeRecordFromPerformanceScore(performanceScore);
      const scoringMechanism = lessonPlayerStore.getIsPitchDetectionEnabled() ? "PITCH_DETECTION" : "MIDI";
      const addPracticeRecordResponse = await playgroundApi.addPracticeRecord({practiceRecord}, scoringMechanism);

      if (addPracticeRecordResponse.practiceRecords[0]?.isSectionComplete && addPracticeRecordResponse.practiceRecords[0]?.isFirstTimeSectionComplete) {
        lessonPlayerStore.setDisplaySectionEndModal(true);
        lessonPlayerStore.setDisplayScoreModal(false);
      }

      if (this.lesson && this.segment) {
        if (!this.isUserOnLessonPage()) { return; }

        const scoreModalViewModel = createScoreModalViewModel(addPracticeRecordResponse, this.lesson, this.segment);
        //tell the ui to update
        lessonPlayerStore.setScoreModalViewModel(scoreModalViewModel);
        await this.updateSegmentDropdownScores();
      }
    }, "We ran into an error saving your practice record");
  }

  /**
   * When a practice record is added, we want to update the scores in the dropdown, so they reflect the score the user just received.
   */
  async updateSegmentDropdownScores() {
    if (!this.lesson) { return; }

    if(!this.isUserOnLessonPage()) { return; }

    const lessonId = parseInt(this.lesson.id);
    const lessonResult = await playgroundApi.getLesson({lessonId});

    if(!this.isUserOnLessonPage()) { return; }

    const lesson = createLessonViewModelFromApiResponse(lessonResult, undefined);
    this.lesson.segments = lesson.segments;
    lessonPlayerStore.setSegments(this.lesson.segments);
  }

  _attemptsToFixOffset0Bug = 0;
  /**
   * Handle certain messages from Soundslice, like ssNotationLoaded, ssGetNotes, etc.
   * @param message
   */
  onMessageFromSoundslice_received(message: SoundsliceResponseEventTypes){
    //when the segment mode is followAlong, we need to get the notes with synchronized times.  Otherwise, always send 0.
    const recordingIndex = this.segment?.mode === "followAlong" ? this.segment?.recordingIndex : 0;
    switch (message.type) {
      case "ssNotationLoaded": //wait for iframe to load, then get info about the segment
        this._attemptsToFixOffset0Bug = 0;
        logger.lesson.log({message: `getting notes for recording index: ` + recordingIndex});
        iframeGetNotes(recordingIndex);

        break;
      case "ssGetNotes":
        if(this._areAllNotesOffset0BugCheck(message.notes)){
          setTimeout(()=>{
            if(this._attemptsToFixOffset0Bug++ < 20){
              iframeGetNotes(recordingIndex);
            }else{
              const msg = 'Could not recover from all notes having offset of 0.';
              logger.lesson.error({message: msg});
              displayErrorToUser("",[`${msg}  Please reload the segment.`]);
            }
          }, 100);
        }else{
          this.setNotes(message.notes);
        }
        break;
    }
  }

  _areAllNotesOffset0BugCheck(notes: Note[]){
    if(notes.length <= 0){ return false; }
    for(let i = 0; i < notes.length; ++i){
      const note = notes[i];
      if(note.offset !== 0){ return false; }
    }
    return true;
  }

  /**
   * When a route changes (e.g. user goes from courses page to lesson page, or segment is switched in a lesson), we need to
   * - stop playing any existing music pieces.
   * - fetch lesson data, if it's not already in memory. e.g. url /lesson/{lessonId}/{segmentId} should fetch the lesson with the lesson id in the path.
   * - get and set segment data based on the segment id in the url. e.g. url /lesson/{lessonId}/{segmentId} should get segment info for the segment id in the path.
   * - notify metronome, notesPlayer, etc that segment data has changed. e.g. notesPlayer needs the notes for the segment.  metronome needs tempo, measures, etc.
   * @param routePath - path that the route specifies for url matching.  e.g. "/lesson/:id?/:segmentId?"
   * @param locationPath - path that the browser is currently on e.g. "/lesson/1245/32"
   * @param search - query string params
   * @param userHasValidToken
   */
  async onRoute_changed({routePath, locationPath, search, userHasValidToken}: IRouteChanged){
    if (routePath === appRoutePaths.lastPageUsed) {
      // Ignore route changes to the last page used path - this method will get called again with the proper route.
      return;
    }

    // Detect fresh app load vs in-app navigation
    // Fresh load: previousLocationPath is undefined (first route after app start, excluding lastPageUsed which returns early above)
    // In-app navigation: previousLocationPath has a value
    const isFreshLoad = this.previousLocationPath === undefined;

    // Check if the previous page included "/lesson/"
    const wasOnLessonPage = this.previousLocationPath?.indexOf(lessonPageUrlFragment) !== -1;
    const isOnLessonPage = routePath.indexOf(lessonPageUrlFragment) !== -1;
    const isNavigatingAwayFromLessonPage = wasOnLessonPage && !isOnLessonPage;
    const isNavigatingToLessonPage = !wasOnLessonPage && isOnLessonPage;
    const hasStayedOnLessonPage = wasOnLessonPage && isOnLessonPage;

    // Update previousLocationPath for the next route change
    this.previousLocationPath = locationPath;

    if (hasStayedOnLessonPage || isNavigatingToLessonPage) {
      this.#onLessonPage_enter(isFreshLoad);
    }

    return withDisplayErrorToUser(async ()=> {
      //tell everyone to stop playing if we leave the lesson page.
      if(isNavigatingAwayFromLessonPage || !userHasValidToken) {
        return this.onLessonPage_leave();
      }

      //parse out the lesson id and segment id from the url
      if (isOnLessonPage) {
        const {
          lessonId: lessonIdString,
          segmentId,
          mode,
          courseId,
          referrer
        } = getCurrentLessonIdSegmentIdAndCourseIdAndReferrerFromLocation(locationPath, search);
        void await this.loadLessonAndSegment({lessonIdString, segmentId, mode, courseId, referrer});

        this.#incrementMetricsSegmentsOpenedCountIfApplicable();
      }
    }, "An error occurred loading lesson");
  }

  // Consider trying to remove this method & its usages, since it uses document.getElementById, which has friction with the React paradigm
  async #updateIndicator(id: string, valueFunction: () => Promise<boolean>, attemptNumber: number = 1) {
    const isEnabled = await valueFunction();

    //get current time to the millisecond
    const element = <HTMLElement>document.getElementById(id);
    if (element) {
      element.style.backgroundColor = isEnabled ? "green" : "white";
    } else {
      if (attemptNumber <= 3) {
        console.warn(`Could not find ${id} element. Trying again in 1 second.`);
        setTimeout(() => {
          this.#updateIndicator(id, valueFunction, attemptNumber + 1);
        }, 1000);
      } else {
        console.error(`Could not find ${id} element.`);
      }
    }
  }

  /**
   * Fetches new lesson if lesson id is different than the one in memory.  Typically called when a route changes.
   * @param lessonIdString - id of the lesson to load.
   * @param segmentId - id of the segment to load (and notify others about. e.g. metronome, notesPlayer)
   * @param mode - The same segment id may appear twice: once for practice and once for perform
   * @param courseId
   * @param referrer
   */
  async loadLessonAndSegment({lessonIdString, segmentId, mode, courseId, referrer}: {lessonIdString: string; segmentId?: string, mode?: SegmentModeEnum, courseId: string | undefined, referrer: string | undefined}){
    logger.lesson.log({message: `loadLesson lessonId: ${lessonIdString} segmentId: ${segmentId} mode: ${mode} referrer: ${referrer}`});
    if(referrer){
      setItem(lessonPageReferrerLocalStorageKey, referrer);
      lessonPlayerStore.setReferrer(referrer);
    }else{
      const referrer = getItem(lessonPageReferrerLocalStorageKey);
      if(referrer){
        lessonPlayerStore.setReferrer(referrer);
      }
    }

    lessonPlayerStore.setShowPermissionDeniedModal(false);

    // If the lesson is not cached for the lesson id, fetch the lesson data.
    if(this.lesson === undefined || this.lesson.id !== lessonIdString){
      lessonPlayerStore.setIsLessonLoading(true);
      const lessonId = parseInt(lessonIdString);
      let lessonResponse: ILessonResponse | undefined;
      try {
        lessonResponse = await playgroundApi.getLesson({lessonId});
      } catch (e: unknown) {
        if (e instanceof LessonPermissionsError) {
          logger.lesson.error({message: `LessonPermissionsError: ${e.message}`, error: e, data: [{key: 'lessonId', value: lessonId}]});
          Sentry.addBreadcrumb({
            category: 'lesson',
            message: `LessonPermissionsError for lessonId ${lessonId}`,
            level: 'warning',
          });
          lessonPlayerStore.setShowPermissionDeniedModal(true);
          return;
        }
        throw e;
      }

      if (lessonResponse) {
        const lesson = createLessonViewModelFromApiResponse(lessonResponse, courseId);
        this.setLesson(lesson);
      }
    }
    // Find the segment by id and optionally by mode, if it is present.  Mode won't be present when linking from the course/bootcamp/songs page, but should be present when navigating inside the Segment page.
    const segment = this.lesson && segmentId !== undefined ? this.lesson.segments.find(s => s.id === segmentId && (mode === undefined || s.mode === mode)) : undefined;

    if(!segment){ // Initially did this to update the segment selector dropdown, but didn't work, but may be nice to have...
      const segmentId = this.lesson?.segments[0]?.id;
      const url = createLessonUrl(lessonIdString, segmentId, undefined, mode, referrer);
      return navigate(url, true);
    }

    // Since we never mutate the segment in the lessonPage.segments array, we always need to get the setting when the segment changes.
    if(segment){
      segment.shouldHideSegmentDescription = settingsService.getShouldHideSegmentDescription(segment.id);
    }
    setScrollTypeAndVideoPositionBasedOnOrientation(segment);
    this.setSegment(segment);

    const normalSpeedOption = segment.speedOptions.filter((speedOption) => {
      return speedOption.value.toFixed(0) === "1";
    })[0];
    const otherSpeedOption = segment.speedOptions.filter((speedOption) => {
      return normalSpeedOption && speedOption.mp3Url !== normalSpeedOption.mp3Url;
    })[0];
    const normalMp3Url = normalSpeedOption?.mp3Url ?? "";
    const slowMp3Url = otherSpeedOption?.mp3Url ?? "";

    nativeBridge.sendMessageToNative({type: "audio.backingTrackUrl_changed", url: normalMp3Url, trackType: "normal", timestamp: Date.now()});
    nativeBridge.sendMessageToNative({type: "audio.backingTrackUrl_changed", url: slowMp3Url, trackType: "slow", timestamp: Date.now()});
  }

  /**
   * When a user navigates away from the lesson page, cleanup cached data, let metronome and notesPlayer know, etc.
   */
  onLessonPage_leave(){
    this.setLesson(undefined); //clear cached lesson data so that it is refetched when they come back.
    this.setSegment(undefined); //notify metronome, notesPlayer, etc that they should stop playing.

    lessonPlayerStore.setShowPermissionDeniedModal(false);
    lessonPlayerStore.setSpeed(1);

    // Unsubscribe from MIDI events so the lesson service doesn't respond
    // to device changes when not on the lesson page
    if (this.#unsubscribeFromMidiEvents) {
      this.#unsubscribeFromMidiEvents();
      this.#unsubscribeFromMidiEvents = null;
    }
  }

  #onLessonPage_enter(isFreshLoad: boolean): void {
    logger.pitchDetection.info({
      message: "onLessonPage_enter",
      showStack: false
    });

    segmentDataProvider.resetAllSubscribers();
    this.#syncLocalStorageValuesWithRedux();
    this.#setupReduxListeners();
    this.pitchDetector = new PitchDetector(this.onNotes_detected, this.onNoteTranscribing_update);
    this.#setupMidiDevicesListener(isFreshLoad);
  }

  #incrementMetricsSegmentsOpenedCountIfApplicable() {
    let isInAVideoOrFollowAlong = false;
    const segment = lessonPlayerStore.getSegment();
    if (segment) {
      isInAVideoOrFollowAlong = segment.mode === SegmentModeEnum.play || segment.mode === SegmentModeEnum.followAlong;
    }

    if (!isInAVideoOrFollowAlong && segment) {
      lessonPlayerStore.incrementMetricsSegmentsOpenedCount();
    }
  }

  onNotes_detected(notes: NoteEventTime[]) {
    // Need to throw out all the detected notes that occurred before scoring began
    const scoringStartTimeInMs = lessonPlayerStore.getScoringStartTimeInMs();
    const recordingStartTimeInMs = lessonPlayerStore.getRecordingStartTimeInMs();

    if (!scoringStartTimeInMs || !recordingStartTimeInMs) {
      throw new Error("Scoring start time or recording start time not set.");
    }

    // Calculate time difference in ms between recording start time and scoring start time
    const timeDifferenceInSeconds = (scoringStartTimeInMs - recordingStartTimeInMs) / 1000;

    // Ideally we would use correctThresholdFloor here instead of a hard coded 100. Can't though because this method is a callback and correctThresholdFloor is undefined
    const timeCutOff = timeDifferenceInSeconds - 100;

    const workingCopy = deepCopyArray(notes);

    sortNotesByPitchThenByStartTime(notes);
    // combineDuplicateNotes(notes);

    // There is a window of time between when the microphone recording starts and the scoring begins. We don't care about
    // any notes detected in this time period and they should be thrown away
    const notesPlayedAfterScoringStarted = workingCopy.filter((note) => note.startTimeSeconds >= timeCutOff);

    // Need to account for time it takes for signal from keyboard / piano to reach microphone. Also need to account for attack envelope and delayed note
    // registration due to noise suppression. Testing on my machine seems to take about 135ms.
    const keyboardToMicrophoneDelay = .135;

    // Shift all the start times of the remaining notes to be relative to the scoring start time instead of the recording start time
    // Also account for the delay between the keyboard and the microphone
    notesPlayedAfterScoringStarted.forEach((note) => {
      note.startTimeSeconds -= timeDifferenceInSeconds + keyboardToMicrophoneDelay;
    });

    noteScorer.scoreNotes(notesPlayedAfterScoringStarted);
  }

  onNoteTranscribing_update(percentageComplete: number) {
    const percentage = (percentageComplete * 100).toFixed(0).toString();
    transcriptionBenchmark.markTranscriptionProgress(percentage);
    lessonPlayerStore.setTranscriptionProgressPercentage(percentage);
  }

  /**
   * Set value of cached lesson.
   * @param lesson
   */
  setLesson(lesson?: Lesson){
    this.lesson = lesson;
    logger.lesson.log({message: `lesson is: ` + this.lesson});
    lessonPlayerStore.setLessonLoaded(this.lesson);
    lessonLoadedEvent();// Tell settings to reset isBackingTrackEnabled
  }

  /**
   * Set the segment and emit "segmentChanged" and "segmentNotesChanged" events.
   * @param segment - (nullable) segment to reference
   */
  setSegment(segment?: Segment){
    this.segment = segment;
    logger.lesson.log({message: `segment is: ` + segment});
    segmentChanged(this.segment); //let metronome recalculate time signature based on segment.measures
    this.setNotes(undefined); //reset the notes anytime the segment changes, and count on ssGetNotes ss event.
    lessonPlayerStore.setSegment(this.segment); //let the ui know so the iframe url can change.
    if(segment && segment.courseId !== undefined){
      playgroundApi.courseSegmentVisit({segmentId: segment.id, courseId: segment.courseId}).catch((e)=>{
        logger.lesson.error({message: `call to course segment visit failed:`, error: e as Error});
      });
    }
  }

  /**
   * Set the notes and emit "segmentNotesChanged" event so metronome, etc can change behavior accordingly.
   * @param notes
   */
  setNotes(notes?: Note[]){
    this.notes = notes;
    logger.lesson.debug({message: `notes: ` + notes});
    segmentNotesChanged(this.notes); //let the notesplayer know there are new notes.
  }
}

/**
 * Parses the current segment id from the current pathname.
 * Parsing the url is useful for deep linking. e.g. a bookmark or a link from other place in the app.
 * @param pathname - history.location.pathname e.g. "/lesson/12345/5555"
 * @param search
 */
function getCurrentLessonIdSegmentIdAndCourseIdAndReferrerFromLocation(pathname: string, search: string | undefined){
  const parts = pathname.split("/"); //should result in ["", "lesson", "12345", "5555", "performance"] NOTE: the last part, mode, is optional, as the courses/bootcamp/song page isn't aware of the mode when it links to the segment page.

  const courseIdQueryStringPossibleMatch = search ? search.match(/courseId=([^&]*)/) : undefined;
  const courseId = courseIdQueryStringPossibleMatch && courseIdQueryStringPossibleMatch.length > 1 ? courseIdQueryStringPossibleMatch[1] : undefined;

  const referrerQueryStringPossibleMatch = search ? search.match(/referrer=([^&]*)/) : undefined;
  const referrer = referrerQueryStringPossibleMatch && referrerQueryStringPossibleMatch.length > 1 ? referrerQueryStringPossibleMatch[1] : undefined;

  if(parts.length < 3){
    return {lessonId: "", segmentId: "", courseId};
  }
  let mode;
  if(parts.length >= 4){
    mode = parts[4] as SegmentModeEnum;
  }
  return {lessonId: parts[2], segmentId:  parts[3], mode, courseId, referrer};
}

function createPracticeRecordFromPerformanceScore(performanceScore: IPerformanceScore): AddPracticeRecordRequest{
  return new AddPracticeRecordRequest({
    id: 0, //always 0
    lessonId: parseInt(performanceScore.lessonId),
    segmentId: parseInt(performanceScore.segmentId),
    progress: 0, //always 0 when sent from client. has value populated on backend? db shows recent progress over 0.
    metronome: false, //always 0
    noteScore: performanceScore.songScore,
    scorePercentage: performanceScore.roundedScorePercentage,
    hands: performanceScore.hands,
    recorded: false,
    duration: performanceScore.scoringDurationMs,
    missedNotes: performanceScore.totalMissedNotes,
    longestStreak: 0, //always 0
    starScore: performanceScore.starsScore,
    rhythmAccNotes: performanceScore.totalNumberOfNotesPlayedWhereRhythmWasAccurate,
    rhythmInAccNotes: performanceScore.totalNumberOfNotesPlayedWhereRhythmWasInaccurate,
    date: performanceScore.date,
    wrongNotes: performanceScore.totalWrongNotes,
    tempo: performanceScore.tempo,
    bonusScore: 0, //always 0 stopped having value may 2015
    mode: performanceScore.segmentMode,
    totalNotes: performanceScore.totalNumberOfNotesInSegment,
    totalScore: performanceScore.totalPerformanceScore,
    difficulty: 0, //always 0 stopped having value may 2015
  });
}

function createPracticeRecordFromVideoScore(videoScore: IVideoScore): AddPracticeRecordRequest{
  return new AddPracticeRecordRequest({
    id: 0, //always 0
    lessonId: parseInt(videoScore.lessonId),
    segmentId: parseInt(videoScore.segmentId),
    progress: 0, //always 0 when sent from client. has value populated on backend? db shows recent progress over 0.
    metronome: false, //always 0
    noteScore: 0, //legacy sens NaN
    scorePercentage: 0,
    hands: videoScore.hands,
    recorded: false,
    duration: videoScore.durationMs,
    missedNotes: 0,
    longestStreak: 0, //always 0
    starScore: 0,
    rhythmAccNotes: 0,
    rhythmInAccNotes: 0,
    date: videoScore.date,
    wrongNotes: 0,
    tempo: 1,
    bonusScore: 0, //always 0 stopped having value may 2015
    mode: videoScore.segmentMode,
    totalNotes: 0,
    totalScore: 0, //legacy sends NaN
    difficulty: 0, //always 0 stopped having value may 2015
  });
}

function createScoreModalViewModel(addPracticeRecordResponse: AddPracticeRecordResponse, lesson: Lesson, currentSegment: Segment){
  const practiceRecord = addPracticeRecordResponse.practiceRecords[0];
  if(!practiceRecord){ throw new Error("No practice record received from server"); }
  const shouldDisplayStars = currentSegment.mode === SegmentModeEnum.perform;

  // const starsAwarded = addPracticeRecordResponse.starsAwarded;
  const starsAwarded = addPracticeRecordResponse.practiceRecords[0]?.starScore >= 0 ? addPracticeRecordResponse.practiceRecords[0]?.starScore : addPracticeRecordResponse.starsAwarded;
  const didLevelUp = addPracticeRecordResponse.user.didLevelUp;
  const level = addPracticeRecordResponse.user.level;
  const hasUnlockedSongs = addPracticeRecordResponse.practiceRecords[0].hasUnlockedSongs;

  /** from legacy
   var correctNotes:Number = (record.rhythmAccNotes / (record.numOfNotesPlayed + record.missedNotes)) * 100;
   var closeNotes:Number = (record.rhythmInAccNotes / (record.numOfNotesPlayed + record.missedNotes)) * 100;
   var missedNotes:Number = ((record.wrongNotes + record.missedNotes) / (record.numOfNotesPlayed + record.missedNotes)) * 100;
   */
  const isVideoOnlySegment = (currentSegment.hasVideo && currentSegment.measures.length <= 0);
  const shouldDisplayAccuracy = !isVideoOnlySegment;
  const rhythmAccurateNotes = practiceRecord.rhythmAccNotes; //correct
  const rhythmInaccurateNotes = practiceRecord.rhythmInAccNotes; //close
  const totalMissedNotes = practiceRecord.missedNotes; //not played
  const wrongNotes = practiceRecord.wrongNotes; //extraneous notes

  const totalNotesPlayed = rhythmAccurateNotes + rhythmInaccurateNotes + wrongNotes;

  const missedScorePercentage = ( (wrongNotes + totalMissedNotes) / (totalNotesPlayed + totalMissedNotes)) * 100;
  const roundedMissedScorePercentage = isNaN(missedScorePercentage) ? 0 : Math.round(missedScorePercentage);

  const closeScorePercentage = (rhythmInaccurateNotes / (totalNotesPlayed + totalMissedNotes)) * 100;
  const roundedCloseScorePercentage = isNaN(closeScorePercentage) ? 0 : Math.round(closeScorePercentage);

  const correctNotesPercent = practiceRecord.scorePercentage;
  const missedNotesPercent = roundedMissedScorePercentage;
  const closeNotesPercent = roundedCloseScorePercentage;
  const progressDirection = practiceRecord.progress === 0 ? undefined : practiceRecord.progress > 0 ? "up" : "down";

  const pointsAwarded = addPracticeRecordResponse.performanceSessionPointsAwarded + addPracticeRecordResponse.timeSessionPointsAwarded;   //PerformanceSessionPointsAwarded
  const nextLevel = addPracticeRecordResponse.user.nextLevel;
  const pointsUntilNextLevel = addPracticeRecordResponse.user.pointsUntilNextLevel;

  let nextButtonLink;

  const segmentIndex = lesson.segments.findIndex((s)=> s.id === currentSegment.id && s.mode === currentSegment.mode);
  const hasNextSegment = segmentIndex >= 0 && segmentIndex < lesson.segments.length - 1;
  if(hasNextSegment) {
    nextButtonLink = `/lesson/${lesson.id}/${lesson.segments[segmentIndex+1].id}`;
  }

  const scoreModal: IScoreModal = {
    closeNotesPercent,
    correctNotesPercent,
    didLevelUp,
    hasUnlockedSongs,
    level,
    missedNotesPercent,
    nextLevel,
    pointsAwarded,
    pointsUntilNextLevel,
    progressDirection,
    nextButtonLink,
    shouldDisplayAccuracy,
    shouldDisplayStars,
    starsAwarded,
  };
  return scoreModal;
}

export function setScrollTypeAndVideoPositionBasedOnOrientation(segment: Segment | undefined) {
  if (!segment) {
    return;
  }

  const shouldOnlyChangeVideoPosition = segment.mode === SegmentModeEnum.followAlong || segment.mode === SegmentModeEnum.play;

  if (shouldOnlyChangeVideoPosition) {
    updateVideoPositionInSoundSlice();
  } else {
    updateScrollTypeAndVideoPositionBasedOnOrientation(segment);
  }
}

function updateScrollTypeAndVideoPositionBasedOnOrientation(segment: Segment) {
  const isPlaying = lessonPlayerStore.getIsPlaying();
  const orientation = getOrientation();

  // We don't want to change the vertical/horizontal scroll if the segment is playing.
  if (!isPlaying) {
    //video
    lessonPlayerStore.setForceSideVideo(orientation === "landscape");
    lessonPlayerStore.setForceTopVideo(!segment.lessonPlayerSettings.forceTopVideo);

    //scroll

    //TODO: using the incorrect recording index was a gotcha. The appropriate recording index of 0 should be set in the lessonPageFactory.
    // Currently it's only in createSegmentViewModelFromApiResponse, but it needs to be set on the segment.
    const normalRecordingOption = segment.speedOptions.find(s => s.label === "Normal");
    const normalRecordingIndex = normalRecordingOption?.recordingIndex || 0;
    const iframeRecordingIndex = segment.hasVideo ? normalRecordingIndex : 0;

    const newLayout = getNotationLayoutToApplyFromSelectedNotationLayout();
    lessonPlayerStore.setAppliedNotationLayout(newLayout);

    const soundsliceIframeUrl = createSoundsliceIframeUrl(segment.lessonPlayerSettings, segment.baseEmbedUrl, iframeRecordingIndex, segment.segmentIndex || 0);
    lessonPlayerStore.setSegmentSoundSliceIFrameUrl(soundsliceIframeUrl);
  }
}

function updateVideoPositionInSoundSlice(){
  const orientation = getOrientation();
  const newSSVideoPosition = orientation === "landscape" ? SoundSliceVideoPositionEnum.VideoLeftOfNotation : SoundSliceVideoPositionEnum.VideoAboveNotation;
  soundSliceSdk_setVideoPosition(newSSVideoPosition);
}

//singleton
const lessonService = new LessonService();
export default lessonService;